import React, { useState } from "react";
import "./Page.css";
import { useNavigate } from "react-router-dom";
import { savePartner } from "../services/PartnerService";
import Swal from "sweetalert2";

import {
    Building2,
    Users,
    GraduationCap,
    BriefcaseBusiness,
    CheckCircle2,
    ArrowRight,
    Globe2,
    Handshake,
    Award,
} from "lucide-react";

function PartnerRegister() {
    const navigate = useNavigate();

    const initialFormData = {
        companyName: "",
        companyType: "",
        website: "",
        gst: "",
        pan: "",

        name: "",
        designation: "",
        mobile: "",
        email: "",

        state: "",
        city: "",
        address: "",
        pincode: "",
    };

    const [formData, setFormData] = useState(initialFormData);
    const [loading, setLoading] = useState(false);

    /* =========================================================
       HANDLE INPUT CHANGE
    ========================================================= */

    const handleChange = (e) => {
        const { name, value } = e.target;

        let updatedValue = value;

        // Mobile: only numbers
        if (name === "mobile") {
            updatedValue = value.replace(/\D/g, "").slice(0, 10);
        }

        // Pincode: only numbers
        if (name === "pincode") {
            updatedValue = value.replace(/\D/g, "").slice(0, 6);
        }

        // GST uppercase
        if (name === "gst") {
            updatedValue = value.toUpperCase().replace(/\s/g, "").slice(0, 15);
        }

        // PAN uppercase
        if (name === "pan") {
            updatedValue = value.toUpperCase().replace(/\s/g, "").slice(0, 10);
        }

        // Remove leading spaces
        if (
            name === "companyName" ||
            name === "name" ||
            name === "designation" ||
            name === "state" ||
            name === "city"
        ) {
            updatedValue = value.replace(/^\s+/, "");
        }

        setFormData((prev) => ({
            ...prev,
            [name]: updatedValue,
        }));
    };

    /* =========================================================
       VALIDATION HELPER
    ========================================================= */

    const showValidationError = (message, fieldName = null) => {
        Swal.fire({
            icon: "warning",
            title: "Please Check Your Details",
            text: message,
            confirmButtonText: "OK",
            confirmButtonColor: "#7c3aed",
            background: "#ffffff",
            allowOutsideClick: false,
        }).then(() => {
            if (fieldName) {
                const field = document.querySelector(
                    `[name="${fieldName}"]`
                );

                if (field) {
                    field.focus();
                }
            }
        });
    };

    /* =========================================================
       FORM VALIDATION
    ========================================================= */

    const validateForm = () => {
        const companyName = formData.companyName.trim();
        const companyType = formData.companyType.trim();
        const website = formData.website.trim();
        const gst = formData.gst.trim().toUpperCase();
        const pan = formData.pan.trim().toUpperCase();

        const name = formData.name.trim();
        const designation = formData.designation.trim();
        const mobile = formData.mobile.trim();
        const email = formData.email.trim();

        const state = formData.state.trim();
        const city = formData.city.trim();
        const address = formData.address.trim();
        const pincode = formData.pincode.trim();

        /* ---------------------------------------------
           COMPANY NAME
        --------------------------------------------- */

        if (!companyName) {
            showValidationError(
                "Please enter your company name.",
                "companyName"
            );
            return false;
        }

        if (companyName.length < 2) {
            showValidationError(
                "Company name must contain at least 2 characters.",
                "companyName"
            );
            return false;
        }

        /* ---------------------------------------------
           COMPANY TYPE
        --------------------------------------------- */

        if (!companyType) {
            showValidationError(
                "Please select your company type.",
                "companyType"
            );
            return false;
        }

        /* ---------------------------------------------
           WEBSITE
        --------------------------------------------- */

        if (website) {
            try {
                const url = new URL(website);

                if (!["http:", "https:"].includes(url.protocol)) {
                    throw new Error();
                }
            } catch {
                showValidationError(
                    "Please enter a valid website URL, for example https://example.com.",
                    "website"
                );
                return false;
            }
        }

        /* ---------------------------------------------
           GST
        --------------------------------------------- */

        if (gst) {
            const gstRegex =
                /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

            if (!gstRegex.test(gst)) {
                showValidationError(
                    "Please enter a valid 15-character GST number.",
                    "gst"
                );
                return false;
            }
        }

        /* ---------------------------------------------
           PAN
        --------------------------------------------- */

        if (pan) {
            const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

            if (!panRegex.test(pan)) {
                showValidationError(
                    "Please enter a valid 10-character PAN number.",
                    "pan"
                );
                return false;
            }
        }

        /* ---------------------------------------------
           CONTACT NAME
        --------------------------------------------- */

        if (!name) {
            showValidationError(
                "Please enter the contact person's name.",
                "name"
            );
            return false;
        }

        if (name.length < 2) {
            showValidationError(
                "Contact person's name must contain at least 2 characters.",
                "name"
            );
            return false;
        }

        const nameRegex = /^[A-Za-z\s.'-]+$/;

        if (!nameRegex.test(name)) {
            showValidationError(
                "Contact person's name should contain only letters.",
                "name"
            );
            return false;
        }

        /* ---------------------------------------------
           DESIGNATION
        --------------------------------------------- */

        if (!designation) {
            showValidationError(
                "Please enter the contact person's designation.",
                "designation"
            );
            return false;
        }

        if (designation.length < 2) {
            showValidationError(
                "Designation must contain at least 2 characters.",
                "designation"
            );
            return false;
        }

        /* ---------------------------------------------
           MOBILE
        --------------------------------------------- */

        if (!mobile) {
            showValidationError(
                "Please enter your mobile number.",
                "mobile"
            );
            return false;
        }

        if (!/^[6-9][0-9]{9}$/.test(mobile)) {
            showValidationError(
                "Please enter a valid 10-digit Indian mobile number starting with 6, 7, 8 or 9.",
                "mobile"
            );
            return false;
        }

        /* ---------------------------------------------
           EMAIL
        --------------------------------------------- */

        if (!email) {
            showValidationError(
                "Please enter your email address.",
                "email"
            );
            return false;
        }

        const emailRegex =
            /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

        if (!emailRegex.test(email)) {
            showValidationError(
                "Please enter a valid email address.",
                "email"
            );
            return false;
        }

        /* ---------------------------------------------
           STATE
        --------------------------------------------- */

        if (!state) {
            showValidationError(
                "Please enter your state.",
                "state"
            );
            return false;
        }

        if (!/^[A-Za-z\s.'-]+$/.test(state)) {
            showValidationError(
                "State name should contain only letters.",
                "state"
            );
            return false;
        }

        /* ---------------------------------------------
           CITY
        --------------------------------------------- */

        if (!city) {
            showValidationError(
                "Please enter your city.",
                "city"
            );
            return false;
        }

        if (!/^[A-Za-z\s.'-]+$/.test(city)) {
            showValidationError(
                "City name should contain only letters.",
                "city"
            );
            return false;
        }

        /* ---------------------------------------------
           PINCODE
        --------------------------------------------- */

        if (!pincode) {
            showValidationError(
                "Please enter your pincode.",
                "pincode"
            );
            return false;
        }

        if (!/^[1-9][0-9]{5}$/.test(pincode)) {
            showValidationError(
                "Please enter a valid 6-digit pincode.",
                "pincode"
            );
            return false;
        }

        /* ---------------------------------------------
           ADDRESS
        --------------------------------------------- */

        if (!address) {
            showValidationError(
                "Please enter your complete business address.",
                "address"
            );
            return false;
        }

        if (address.length < 10) {
            showValidationError(
                "Complete address must contain at least 10 characters.",
                "address"
            );
            return false;
        }

        return true;
    };

    /* =========================================================
       SUBMIT FORM
    ========================================================= */

    const submitForm = async (e) => {
        e.preventDefault();

        // Validate first
        if (!validateForm()) {
            return;
        }

        /* ---------------------------------------------
           CONFIRMATION ALERT
        --------------------------------------------- */

        const confirmation = await Swal.fire({
            icon: "question",
            title: "Submit Partner Registration?",
            text: "Please confirm that all the information provided is correct.",
            showCancelButton: true,
            confirmButtonText: "Yes, Submit",
            cancelButtonText: "Review Details",
            confirmButtonColor: "#7c3aed",
            cancelButtonColor: "#64748b",
            reverseButtons: true,
            allowOutsideClick: false,
        });

        if (!confirmation.isConfirmed) {
            return;
        }

        setLoading(true);

        try {
            const payload = {
                ...formData,

                companyName: formData.companyName.trim(),
                companyType: formData.companyType.trim(),
                website: formData.website.trim(),
                gst: formData.gst.trim().toUpperCase(),
                pan: formData.pan.trim().toUpperCase(),

                name: formData.name.trim(),
                designation: formData.designation.trim(),
                mobile: formData.mobile.trim(),
                email: formData.email.trim().toLowerCase(),

                state: formData.state.trim(),
                city: formData.city.trim(),
                address: formData.address.trim(),
                pincode: formData.pincode.trim(),
            };

            const response = await savePartner(payload);

            console.log("Partner Saved:", response.data);

            /* ---------------------------------------------
               SUCCESS ALERT
            --------------------------------------------- */

            await Swal.fire({
                icon: "success",
                title: "Registration Successful!",
                text: "Your partner registration has been submitted successfully.",
                confirmButtonText: "Continue",
                confirmButtonColor: "#7c3aed",
                allowOutsideClick: false,
            });

            setFormData(initialFormData);

            navigate("/");
        } catch (error) {
            console.error("Partner Save Error:", error);

            let errorMessage =
                "Unable to complete partner registration. Please try again.";

            if (error.response) {
                const serverData = error.response.data;

                if (typeof serverData === "string") {
                    errorMessage = serverData;
                } else if (serverData?.message) {
                    errorMessage = serverData.message;
                } else if (serverData?.error) {
                    errorMessage = serverData.error;
                }

                /* ---------------------------------------------
                   DUPLICATE DATA
                --------------------------------------------- */

                const lowerMessage = errorMessage.toLowerCase();

                if (
                    lowerMessage.includes("duplicate") ||
                    lowerMessage.includes("already exists") ||
                    lowerMessage.includes("unique")
                ) {
                    errorMessage =
                        "This partner information already exists. Please check the email, GST or PAN details.";
                }
            } else if (error.request) {
                errorMessage =
                    "Unable to connect with the backend server. Please check whether your server is running.";
            } else if (error.message) {
                errorMessage = error.message;
            }

            Swal.fire({
                icon: "error",
                title: "Registration Failed",
                text: errorMessage,
                confirmButtonText: "Try Again",
                confirmButtonColor: "#dc2626",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="partner-page">

            {/* =================================================
                BACKGROUND DECORATION
            ================================================== */}

            <div className="partner-bg-circle partner-bg-circle-one"></div>
            <div className="partner-bg-circle partner-bg-circle-two"></div>

            <div className="partner-wrapper">

                {/* =================================================
                    LEFT INFORMATION PANEL
                ================================================== */}

                <section className="partner-intro">

                    <h1>
                        Become a
                        <span> Partner</span>
                    </h1>

                    <p className="partner-intro-text">
                        Grow your business with CertED Technologies.
                        Partner with us to deliver high-quality
                        training, connect with learners and unlock
                        new business opportunities.
                    </p>

                    {/* Visual Illustration */}

                    <div className="partner-visual">

                        <div className="visual-orbit orbit-a"></div>
                        <div className="visual-orbit orbit-b"></div>
                        <div className="visual-orbit orbit-c"></div>

                        <div className="visual-main-icon">
                            <Handshake size={52} />
                        </div>

                        <div className="floating-icon floating-one">
                            <GraduationCap size={22} />
                        </div>

                        <div className="floating-icon floating-two">
                            <Users size={20} />
                        </div>

                        <div className="floating-icon floating-three">
                            <Award size={20} />
                        </div>

                        <div className="visual-mini-card">
                            <strong>CertED Partner</strong>
                            <span>Build • Train • Grow</span>
                        </div>

                    </div>

                    {/* Benefits */}

                    <div className="partner-benefits">

                        <h3>
                            <span></span>
                            Why Partner With Us?
                        </h3>

                        <div className="benefit-list">

                            <div className="benefit-item">

                                <div className="benefit-icon">
                                    <BriefcaseBusiness size={17} />
                                </div>

                                <div>
                                    <strong>
                                        More Business Opportunities
                                    </strong>

                                    <p>
                                        Get access to new training
                                        requirements and projects.
                                    </p>
                                </div>

                            </div>

                            <div className="benefit-item">

                                <div className="benefit-icon">
                                    <Users size={17} />
                                </div>

                                <div>
                                    <strong>
                                        Wider Network
                                    </strong>

                                    <p>
                                        Connect with colleges,
                                        companies and learners.
                                    </p>
                                </div>

                            </div>

                            <div className="benefit-item">

                                <div className="benefit-icon">
                                    <GraduationCap size={17} />
                                </div>

                                <div>
                                    <strong>
                                        Training Opportunities
                                    </strong>

                                    <p>
                                        Participate in technical and
                                        corporate training programs.
                                    </p>
                                </div>

                            </div>

                            <div className="benefit-item">

                                <div className="benefit-icon">
                                    <Globe2 size={17} />
                                </div>

                                <div>
                                    <strong>
                                        Pan India Reach
                                    </strong>

                                    <p>
                                        Expand your training presence
                                        across multiple locations.
                                    </p>
                                </div>

                            </div>

                        </div>

                    </div>

                    {/* Trust Points */}

                    <div className="partner-trust-row">

                        <div>
                            <CheckCircle2 size={15} />
                            <span>Verified Network</span>
                        </div>

                        <div>
                            <CheckCircle2 size={15} />
                            <span>Professional Support</span>
                        </div>

                        <div>
                            <CheckCircle2 size={15} />
                            <span>Growth Focused</span>
                        </div>

                    </div>

                </section>

                {/* =================================================
                    RIGHT REGISTRATION FORM
                ================================================== */}

                <section className="partner-card">

                    <div className="form-top">

                        <div className="form-top-icon">
                            <Building2 size={21} />
                        </div>

                        <div>

                            <span>
                                PARTNER REGISTRATION
                            </span>

                            <h2>
                                Tell Us About Your Company
                            </h2>

                            <p>
                                Fill in your details to join the
                                CertED partner network.
                            </p>

                        </div>

                    </div>

                    <form onSubmit={submitForm}>

                        {/* =================================================
                            COMPANY DETAILS
                        ================================================== */}

                        <div className="form-section-title">

                            <div className="section-title-icon">
                                <Building2 size={14} />
                            </div>

                            <div>
                                <h3>Company Details</h3>

                                <span>
                                    Basic information about your organization
                                </span>
                            </div>

                        </div>

                        <div className="grid">

                            <div>

                                <label>
                                    Company Name
                                    <span className="required">*</span>
                                </label>

                                <input
                                    type="text"
                                    name="companyName"
                                    value={formData.companyName}
                                    onChange={handleChange}
                                    placeholder="Enter company name"
                                    minLength="2"
                                    required
                                />

                            </div>

                            <div>

                                <label>
                                    Company Type
                                    <span className="required">*</span>
                                </label>

                                <select
                                    name="companyType"
                                    value={formData.companyType}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="">
                                        Select company type
                                    </option>

                                    <option value="Training Partner">
                                        Training Partner
                                    </option>

                                    <option value="Corporate Training Partner">
                                        Corporate Training Partner
                                    </option>

                                    <option value="Education Partner">
                                        Education Partner
                                    </option>

                                    <option value="IT Training Partner">
                                        IT Training Partner
                                    </option>

                                    <option value="Technology Partner">
                                        Technology Partner
                                    </option>

                                </select>

                            </div>

                            <div>

                                <label>
                                    Website
                                </label>

                                <input
                                    type="url"
                                    name="website"
                                    value={formData.website}
                                    onChange={handleChange}
                                    placeholder="https://example.com"
                                />

                            </div>

                            <div>

                                <label>
                                    GST Number
                                </label>

                                <input
                                    type="text"
                                    name="gst"
                                    value={formData.gst}
                                    onChange={handleChange}
                                    placeholder="15 character GST number"
                                    maxLength="15"
                                />

                            </div>

                            <div>

                                <label>
                                    PAN Number
                                </label>

                                <input
                                    type="text"
                                    name="pan"
                                    value={formData.pan}
                                    onChange={handleChange}
                                    placeholder="10 character PAN number"
                                    maxLength="10"
                                />

                            </div>

                        </div>

                        {/* =================================================
                            CONTACT DETAILS
                        ================================================== */}

                        <div className="form-section-title">

                            <div className="section-title-icon">
                                <Users size={14} />
                            </div>

                            <div>

                                <h3>
                                    Contact Person
                                </h3>

                                <span>
                                    Primary person we can contact
                                </span>

                            </div>

                        </div>

                        <div className="grid">

                            <div>

                                <label>
                                    Contact Person Name
                                    <span className="required">*</span>
                                </label>

                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder="Enter contact person name"
                                    minLength="2"
                                    required
                                />

                            </div>

                            <div>

                                <label>
                                    Designation
                                    <span className="required">*</span>
                                </label>

                                <input
                                    type="text"
                                    name="designation"
                                    value={formData.designation}
                                    onChange={handleChange}
                                    placeholder="e.g. HR Manager"
                                    minLength="2"
                                    required
                                />

                            </div>

                            <div>

                                <label>
                                    Mobile Number
                                    <span className="required">*</span>
                                </label>

                                <input
                                    type="tel"
                                    name="mobile"
                                    value={formData.mobile}
                                    onChange={handleChange}
                                    placeholder="10 digit mobile number"
                                    maxLength="10"
                                    inputMode="numeric"
                                    required
                                />

                            </div>

                            <div>

                                <label>
                                    Email Address
                                    <span className="required">*</span>
                                </label>

                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="company@email.com"
                                    required
                                />

                            </div>

                        </div>

                        {/* =================================================
                            ADDRESS DETAILS
                        ================================================== */}

                        <div className="form-section-title">

                            <div className="section-title-icon">
                                <Globe2 size={14} />
                            </div>

                            <div>

                                <h3>
                                    Business Location
                                </h3>

                                <span>
                                    Where your organization operates
                                </span>

                            </div>

                        </div>

                        <div className="grid">

                            <div>

                                <label>
                                    State
                                    <span className="required">*</span>
                                </label>

                                <input
                                    type="text"
                                    name="state"
                                    value={formData.state}
                                    onChange={handleChange}
                                    placeholder="Enter state"
                                    required
                                />

                            </div>

                            <div>

                                <label>
                                    City
                                    <span className="required">*</span>
                                </label>

                                <input
                                    type="text"
                                    name="city"
                                    value={formData.city}
                                    onChange={handleChange}
                                    placeholder="Enter city"
                                    required
                                />

                            </div>

                            <div>

                                <label>
                                    Pincode
                                    <span className="required">*</span>
                                </label>

                                <input
                                    type="text"
                                    name="pincode"
                                    value={formData.pincode}
                                    onChange={handleChange}
                                    placeholder="6 digit pincode"
                                    maxLength="6"
                                    inputMode="numeric"
                                    required
                                />

                            </div>

                            <div className="full">

                                <label>
                                    Complete Address
                                    <span className="required">*</span>
                                </label>

                                <textarea
                                    name="address"
                                    value={formData.address}
                                    onChange={handleChange}
                                    placeholder="Enter complete business address"
                                    minLength="10"
                                    required
                                />

                            </div>

                        </div>

                        {/* =================================================
                            SUBMIT
                        ================================================== */}

                        <div className="form-submit-area">

                            <div className="form-security">

                                <CheckCircle2 size={15} />

                                <span>
                                    Your information is safe and
                                    will only be used for partner
                                    verification.
                                </span>

                            </div>

                            <button
                                type="submit"
                                className="submit-btn"
                                disabled={loading}
                            >

                                {loading ? (
                                    <>
                                        <span className="submit-spinner"></span>
                                        Submitting...
                                    </>
                                ) : (
                                    <>
                                        Become a Partner
                                        <ArrowRight size={17} />
                                    </>
                                )}

                            </button>

                        </div>

                    </form>

                </section>

            </div>

        </div>
    );
}

export default PartnerRegister;